import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Presentation, Copy, Trash2, Plus, Eye, ExternalLink, BarChart3, Mail, MessageSquare, Phone, Flame, Clock, ChevronDown, ChevronUp } from "lucide-react";
import api, { safeCopy, formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-[11px] text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-orange focus:border-nc-orange";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";
const mono9 = "font-mono text-[9px] text-zinc-500";

function fmtReadTime(seconds) {
  if (!seconds) return "0s";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function timeAgo(iso) {
  if (!iso) return "never";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function IntelPanel({ room, lead, messages, onOpenChat }) {
  const [busyKpi, setBusyKpi] = useState(false);

  const logOutreach = async () => {
    setBusyKpi(true);
    try {
      const r = await api.get("/sales/kpi/my?days=1");
      const log = (r.data.logs || []).find((l) => l.date === r.data.today) || {};
      await api.post("/sales/kpi/log", {
        outreach_dms: (log.outreach_dms || 0) + 1,
        links_shared: log.links_shared || 0,
        posts_audited: log.posts_audited || 0,
        active_chats: log.active_chats || 0,
        scopes_drafted: log.scopes_drafted || 0,
      });
      toast.success("Outreach logged to today's KPIs");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyKpi(false);
    }
  };

  const viewLog = [...(room.view_log || [])].reverse().slice(0, 8);

  return (
    <div data-testid={`intel-panel-${room.id}`} className="border-t border-nc-orange/20 bg-[#050505]/80 px-6 py-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
      {/* Lead intel */}
      <div className="flex flex-col gap-2">
        <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase">Lead Intel</p>
        {lead ? (
          <div className="border border-white/10 bg-black/40 p-3 flex flex-col gap-1.5">
            <p className="font-sans font-bold text-sm text-zinc-100">{lead.name}</p>
            <p className={mono9}>{lead.email}</p>
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
              <span className={mono9}>status: <span className="text-zinc-200">{lead.status}</span></span>
              <span className={mono9}>source: <span className="text-zinc-200">{lead.source}</span></span>
              {lead.engine_type && <span className={mono9}>engine: <span className="text-zinc-200">{lead.engine_type}</span></span>}
              {lead.scope_total > 0 && <span className={mono9}>scope: <span className="text-nc-orange">AUD {lead.scope_total}</span></span>}
              <span className={mono9}>since: <span className="text-zinc-200">{String(lead.created_at).slice(0, 10)}</span></span>
            </div>
            {lead.message && <p className={`${mono9} mt-1 leading-relaxed border-t border-white/5 pt-1.5`}>First message: "{String(lead.message).slice(0, 140)}"</p>}
          </div>
        ) : <p className={mono9}>Lead record unavailable.</p>}
        {/* Recent messages */}
        {messages.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase">Latest in channel</p>
            {messages.map((m, i) => (
              <div key={i} className="border border-white/5 bg-black/30 px-2.5 py-1.5">
                <p className="font-mono text-[8px] text-zinc-600 uppercase">{m.sender_name} // {m.sender_kind}</p>
                <p className="font-mono text-[10px] text-zinc-300 leading-relaxed line-clamp-2">{m.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Engagement analytics */}
      <div className="flex flex-col gap-2">
        <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase">Engagement</p>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="border border-white/10 bg-black/40 px-2 py-3"><p className="font-sans font-bold text-base text-zinc-100">{room.views}</p><p className="font-mono text-[8px] text-zinc-500 uppercase">Opens</p></div>
          <div className="border border-white/10 bg-black/40 px-2 py-3"><p className="font-sans font-bold text-base text-nc-orange">{fmtReadTime(room.read_seconds)}</p><p className="font-mono text-[8px] text-zinc-500 uppercase">Read time</p></div>
          <div className="border border-white/10 bg-black/40 px-2 py-3"><p className="font-sans font-bold text-base text-nc-blue">{timeAgo(room.last_viewed_at)}</p><p className="font-mono text-[8px] text-zinc-500 uppercase">Last open</p></div>
        </div>
        <p className="font-mono text-[8px] text-zinc-600 leading-relaxed">
          {room.views === 0 ? "Not opened yet — follow up if it stays cold past 48h." :
           room.read_seconds < 30 ? "Opened but bounced fast — the hook may need work." :
           room.read_seconds > 180 ? "Deep read — they're seriously evaluating. Strike now." :
           "Reading normally — a timely nudge usually converts here."}
        </p>
        {viewLog.length > 0 && (
          <div className="flex flex-col gap-1">
            <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase">Recent reads</p>
            {viewLog.map((v, i) => (
              <div key={i} className="flex items-center justify-between border-b border-white/5 py-1">
                <span className={mono9}><Clock className="w-3 h-3 inline mr-1" />{timeAgo(v.at)}</span>
                <span className="font-mono text-[9px] text-zinc-400">+{fmtReadTime(v.seconds)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reach out */}
      <div className="flex flex-col gap-2">
        <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase">Reach Out</p>
        {lead && (
          <>
            <a data-testid={`intel-email-${room.id}`} href={`mailto:${lead.email}?subject=${encodeURIComponent(`Re: ${room.title}`)}&body=${encodeURIComponent(`Hi ${lead.name},\n\nFollowing up on the proposal I put together for you: ${window.location.origin}/deal/${room.token}\n\n`)}`} className="font-mono text-[10px] tracking-[0.15em] uppercase px-3 py-2.5 border border-nc-orange/40 text-nc-orange hover:bg-nc-orange/10 transition-colors flex items-center gap-2">
              <Mail className="w-3.5 h-3.5" /> Email {lead.name.split(" ")[0]}
            </a>
            <button data-testid={`intel-copy-email-${room.id}`} onClick={async () => { (await safeCopy(lead.email)) ? toast.success("Lead email copied") : toast.error("Copy blocked"); }} className="font-mono text-[10px] tracking-[0.15em] uppercase px-3 py-2.5 border border-white/15 text-zinc-300 hover:border-white/40 transition-colors flex items-center gap-2">
              <Phone className="w-3.5 h-3.5" /> Copy their email
            </button>
            <button data-testid={`intel-open-chat-${room.id}`} onClick={() => onOpenChat?.(lead)} className="font-mono text-[10px] tracking-[0.15em] uppercase px-3 py-2.5 border border-nc-blue/40 text-nc-blue hover:bg-nc-blue/10 transition-colors flex items-center gap-2">
              <MessageSquare className="w-3.5 h-3.5" /> Open dashboard chat
            </button>
            <button data-testid={`intel-log-outreach-${room.id}`} disabled={busyKpi} onClick={logOutreach} className="font-mono text-[10px] tracking-[0.15em] uppercase px-3 py-2.5 border border-nc-green/40 text-nc-green hover:bg-nc-green/10 disabled:opacity-40 transition-colors flex items-center gap-2">
              <Flame className="w-3.5 h-3.5" /> {busyKpi ? "Logging…" : "Log outreach +1 KPI"}
            </button>
          </>
        )}
        <p className="font-mono text-[8px] text-zinc-600 leading-relaxed mt-1">Guide tip: reference something specific from their first message — personalized follow-ups convert, generic ones get binned.</p>
      </div>
    </div>
  );
}

export default function SalesDealRooms({ leads, onOpenChat }) {
  const [rooms, setRooms] = useState([]);
  const [creating, setCreating] = useState(false);
  const [leadId, setLeadId] = useState("");
  const [title, setTitle] = useState("");
  const [intro, setIntro] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState("");
  const [expanded, setExpanded] = useState("");
  const [intel, setIntel] = useState(null);

  const load = useCallback(() => {
    api.get("/sales/deal-rooms").then((r) => setRooms(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const myLeads = (leads || []).filter((l) => !rooms.some((r) => r.lead_id === l.id));

  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post("/sales/deal-rooms", { lead_id: leadId, title, intro });
      toast.success("Deal room live — share the link with your prospect");
      setCreating(false); setLeadId(""); setTitle(""); setIntro("");
      load();
      await safeCopy(r.data.url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/sales/deal-rooms/${id}`);
      toast.success("Deal room taken offline");
      setConfirmDelete("");
      if (expanded === id) setExpanded("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggleIntel = async (id) => {
    if (expanded === id) { setExpanded(""); return; }
    setExpanded(id);
    setIntel(null);
    try {
      const r = await api.get(`/sales/deal-rooms/${id}/intel`);
      setIntel(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="sales-deal-rooms" className="border border-white/10 bg-black/40">
      <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-zinc-100 flex items-center gap-2">
          <Presentation className="w-4 h-4 text-nc-orange" /> Deal Rooms ({rooms.length})
        </h2>
        <button data-testid="deal-room-new-button" onClick={() => setCreating((c) => !c)} className="font-mono text-[9px] tracking-[0.2em] uppercase px-3 py-2 border border-nc-orange/40 text-nc-orange hover:bg-nc-orange/10 transition-colors flex items-center gap-1.5">
          <Plus className="w-3 h-3" /> New Deal Room
        </button>
      </div>

      {creating && (
        <div data-testid="deal-room-create-form" className="border-b border-white/10 p-5 flex flex-col gap-3 bg-[#050505]/60">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Prospect (lead)</label>
              <select data-testid="deal-room-lead-select" className={inputCls} value={leadId} onChange={(e) => setLeadId(e.target.value)}>
                <option value="">Select a lead…</option>
                {myLeads.map((l) => <option key={l.id} value={l.id}>{l.name} — {l.email}{(l.scope_items || []).length > 0 ? ` (scope AUD ${l.scope_total})` : ""}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Title (optional)</label>
              <input data-testid="deal-room-title-input" className={inputCls} placeholder="Project Proposal — Client" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Personal intro (optional)</label>
            <textarea data-testid="deal-room-intro-input" rows={3} className={`${inputCls} resize-y`} placeholder={"A short personal note to the prospect — what you discussed, why this scope fits…"} value={intro} onChange={(e) => setIntro(e.target.value)} maxLength={3000} />
          </div>
          <p className="font-mono text-[9px] text-zinc-600">Saved scope items attach automatically. The prospect can message you straight from the page — replies land in your lead queue.</p>
          <div className="flex gap-2">
            <button data-testid="deal-room-create-submit" disabled={busy || !leadId} onClick={create} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-2.5 transition-colors">
              {busy ? "Creating…" : "Create & Copy Link"}
            </button>
            <button onClick={() => setCreating(false)} className="font-mono text-[9px] uppercase text-zinc-400 px-3">Cancel</button>
          </div>
        </div>
      )}

      {rooms.length === 0 && !creating && (
        <p data-testid="deal-rooms-empty" className="font-mono text-[10px] text-zinc-600 p-5">No deal rooms yet — create one from a lead to get a branded proposal page with built-in chat enquiry.</p>
      )}
      {rooms.map((r, i) => (
        <div key={r.id} data-testid={`deal-room-${i + 1}`}>
          <div className="border-b border-white/5 px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <p className="font-sans font-semibold text-sm text-zinc-100 truncate">{r.title}</p>
              <p className="font-mono text-[9px] text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
                <span>{r.client_name}</span>
                {r.total > 0 && <span>AUD {r.total}</span>}
                <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {r.views} open(s)</span>
                <span className="flex items-center gap-1 text-nc-orange"><Clock className="w-3 h-3" /> {fmtReadTime(r.read_seconds)}</span>
                <span className="text-nc-blue">{r.last_viewed_at ? timeAgo(r.last_viewed_at) : "never opened"}</span>
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid={`deal-room-intel-${i + 1}`} onClick={() => toggleIntel(r.id)} className={`font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border transition-colors flex items-center gap-1.5 ${expanded === r.id ? "border-nc-orange/60 text-nc-orange bg-nc-orange/10" : "border-white/15 text-zinc-300 hover:border-nc-orange/50 hover:text-nc-orange"}`}>
                <BarChart3 className="w-3 h-3" /> Intel {expanded === r.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
              <button data-testid={`deal-room-copy-${i + 1}`} onClick={async () => { (await safeCopy(r.url)) ? toast.success("Deal room link copied") : toast.error("Copy blocked"); }} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-white/15 text-zinc-300 hover:border-nc-orange/50 hover:text-nc-orange transition-colors flex items-center gap-1.5">
                <Copy className="w-3 h-3" /> Copy Link
              </button>
              <a data-testid={`deal-room-open-${i + 1}`} href={r.url} target="_blank" rel="noreferrer" className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-white/15 text-zinc-300 hover:border-nc-blue/50 hover:text-nc-blue transition-colors flex items-center gap-1.5">
                <ExternalLink className="w-3 h-3" /> Open
              </a>
              {confirmDelete === r.id ? (
                <button data-testid={`deal-room-delete-confirm-${i + 1}`} disabled={busy} onClick={() => remove(r.id)} className="font-mono text-[9px] uppercase px-3 py-2 bg-red-500 hover:bg-red-600 text-white transition-colors">Take offline?</button>
              ) : (
                <button data-testid={`deal-room-delete-${i + 1}`} onClick={() => setConfirmDelete(r.id)} className="font-mono text-[9px] uppercase px-3 py-2 border border-red-500/50 text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-1.5">
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
          {expanded === r.id && (
            intel ? (
              <IntelPanel room={intel.room} lead={intel.lead} messages={intel.messages} onOpenChat={onOpenChat} />
            ) : (
              <p className="font-mono text-[10px] text-zinc-600 px-6 py-4 animate-pulse">Loading intel…</p>
            )
          )}
        </div>
      ))}
    </div>
  );
}
